import React, { FC } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import OnboardingHeader from "./OnboardingHeader";
import { getChannelLogo } from "./channelLogos";
import placeholderImg from "images/placeholder-large.png";
import { useLocale } from "./i18n/locale";
import { formatDate } from "./i18n/formatDate";

export interface TenantWelcomePageProps {
  className?: string;
  /** 4 for Flow A (short onboarding), 7 for Flow B (full onboarding). */
  totalSteps?: number;
}

// TODO: the reservation arrives from the DMS via the tenant's secure link.
// Placeholder until that contract exists; values mirror the design mockup.
const RESERVATION = {
  channel: "Blueground",
  photo: placeholderImg,
  property: "Le Marais Elegant",
  address: "17 Rue des Francs Bourgeois, 75004 Paris",
  // Plain calendar dates; formatted per locale at render.
  checkIn: "2026-06-20",
  checkOut: "2026-09-20",
};

interface StepDotsProps {
  current: number;
  total: number;
}

/** Sits on the hero, so the dots are navy on white with a gold active step. */
const StepDots: FC<StepDotsProps> = ({ current, total }) => {
  const { t } = useLocale();

  return (
    <div className="flex items-center">
      {Array.from({ length: total }, (_, i) => i + 1).map((n) => (
        <React.Fragment key={n}>
          {n > 1 && (
            <span
              className="h-px w-3 flex-none bg-white/40"
              aria-hidden="true"
            />
          )}
          <span
            className={`flex h-6 w-6 flex-none items-center justify-center rounded-full text-[11px] font-semibold ${
              n === current
                ? "bg-primary-6000 text-white"
                : "bg-navy-900/70 text-white/80 ring-1 ring-white/30"
            }`}
            aria-hidden="true"
          >
            {n}
          </span>
        </React.Fragment>
      ))}
      <span className="sr-only">{t.common.stepOf(current, total)}</span>
    </div>
  );
};

const TenantWelcomePage: FC<TenantWelcomePageProps> = ({
  className = "",
  totalSteps = 7,
}) => {
  const { locale, t } = useLocale();
  const channelLogo = getChannelLogo(RESERVATION.channel);

  return (
    // lang scopes to the onboarding subtree; see OnboardingShell.
    <div
      lang={locale}
      className={`nc-TenantWelcomePage min-h-screen bg-cream-50 ${className}`}
      data-nc-id="TenantWelcomePage"
    >
      <Helmet>
        <title>{t.welcome.documentTitle}</title>
      </Helmet>

      <div className="mx-auto w-full max-w-md">
        {/* The hero runs full-bleed behind the header and the stepper; the
            booking card then overlaps its lower edge. */}
        <div className="relative">
          <div className="relative overflow-hidden">
            <img
              src={"/living-area.jpg"}
              alt={RESERVATION.property}
              className="h-[26rem] w-full object-cover"
            />
            {/* Gradient runs left-to-right so the copy stays legible over the
                room while the right side of the photo shows through. */}
            <div
              className="absolute inset-0 bg-navy-900/80"
              aria-hidden="true"
            />

            <div className="absolute inset-x-0 top-0 px-5 pt-4">
              <OnboardingHeader showBack={false} variant="light" />
            </div>

            <div className="absolute inset-x-0 bottom-0 px-5 pb-24">
              <h1 className="font-serif text-[34px] leading-[1.1] text-white">
                {t.welcome.titleLine1}
                <br />
                {t.welcome.titleLine2}
              </h1>
              <p className="mt-3 max-w-[15rem] text-[13px] font-semibold leading-relaxed text-primary-6000">
                {t.welcome.subtitle}
              </p>

              <div className="mt-5">
                <StepDots current={1} total={totalSteps} />
              </div>
            </div>
          </div>

          {/* Pulled up over the hero, as in the design. Needs its own stacking
              position: the hero above is relative, so an unpositioned card
              would paint underneath it and lose its top edge. */}
          <div className="relative z-10 -mt-16 px-4 pb-10">
            <div className="overflow-hidden rounded-2xl bg-white shadow-lg">
              <div className="flex flex-col px-4 pb-3 pt-4">
                <span className="text-[11px] leading-tight text-neutral-400">
                  {t.welcome.bookingVia}
                </span>
                {channelLogo ? (
                  // Shapes differ per channel (Immoweb is a thin wordmark,
                  // Vrbo a square mark), so object-contain is the only safe fit
                  // — anything else stretches one of them.
                  <img
                    src={channelLogo}
                    alt={RESERVATION.channel}
                    className="h-10 w-auto max-w-[140px] object-contain object-left"
                  />
                ) : (
                  <span className="text-[15px] font-semibold tracking-wide text-navy-900">
                    {RESERVATION.channel}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-[auto_minmax(0,175px)_1fr] items-center gap-3 border-t border-neutral-100 p-4">
                <img
                  src={"/living-area.jpg"}
                  alt=""
                  className="h-16 w-16 flex-none rounded-lg object-cover"
                />

                <div className="min-w-0">
                  <p className="text-[11px] text-neutral-400">
                    {t.welcome.apartment}
                  </p>
                  <p className="truncate text-[14px] font-semibold text-navy-900">
                    {RESERVATION.property}
                  </p>
                  <p className="mt-0.5 text-[11px] leading-relaxed text-neutral-500">
                    {RESERVATION.address}
                  </p>
                </div>

                <dl className="text-left">
                  <dt className="whitespace-nowrap text-[11px] leading-snug text-neutral-400">
                    {t.welcome.checkIn}
                  </dt>
                  <dd className="whitespace-nowrap text-[13px] font-semibold leading-snug text-navy-900 mt-1">
                    {formatDate(RESERVATION.checkIn, locale)}
                  </dd>
                  <dt className="mt-2 whitespace-nowrap text-[11px] leading-snug text-neutral-400">
                    {t.welcome.checkOut}
                  </dt>
                  <dd className="whitespace-nowrap text-[13px] font-semibold leading-snug text-navy-900 mt-1">
                    {formatDate(RESERVATION.checkOut, locale)}
                  </dd>
                </dl>
              </div>

              <div className="border-t border-neutral-100 px-4 pb-4 pt-4">
                <Link
                  to="/tenant/verify-identity"
                  className="flex w-full items-center justify-between gap-3 rounded-md bg-primary-6000 px-5 py-3.5 text-sm font-semibold tracking-wide text-white transition-colors hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-6000 focus-visible:ring-offset-2"
                >
                  <span className="flex-1 text-center">{t.welcome.cta}</span>
                  <svg
                    className="h-5 w-5 flex-none"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M5 12h18m0 0l-7-7m7 7l-7 7" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantWelcomePage;
